import React, { useState, useEffect } from 'react'
import axios from 'axios';
import jwt_decode from "jwt-decode";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import Loader from "../Loader";
import { useNavigate, Link } from 'react-router-dom';
import formatDateTimeToPST from '../PSTDateConvert';
import Pagination from 'react-js-pagination';
import { saveAs } from 'file-saver';

/**
 * BCP-32: Removed Numbers History.
 *
 * Every number taken out of inventory, whichever entry point was used - the Remove button on the
 * number details page, the bulk Remove on the inventory list, or the Remove Number From Portal
 * import. The existing Import Number Remove History only covers the import and is batch level, so
 * this page is number level and shows the source alongside who did it and which customer it was for.
 */
const RemovedNumbersHistory = () => {
    const [name, setName] = useState('');
    const [token, setToken] = useState('');
    const [expire, setExpire] = useState('');
    const [role, setRole] = useState('');
    const [access, setAccess] = useState([]);
    const [cid, setCid] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const [history, setHistory] = useState([]);
    const [sources, setSources] = useState({});
    const [company, setCompany] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItem, setTotalItem] = useState(0);

    const [selectedCompany, setSelectedCompany] = useState(0);
    const [searchTn, setSearchTn] = useState('');
    const [selectedSource, setSelectedSource] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [callPassOnly, setCallPassOnly] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        refreshToken();
    }, []);

    useEffect(() => {
        if (token) {
            getUserAccess();
            getCompanyList();
            getHistory();
        }
    }, [token, page]);

    useEffect(() => {
        if (role !== '' && role > 2) {
            navigate('/dashboard');
        }
    }, [role]);

    const buildQuery = () => {
        const params = new URLSearchParams({
            page: page,
            limit: 20,
            company: selectedCompany,
            tn: searchTn,
            source: selectedSource,
            fromDate: fromDate,
            toDate: toDate,
            callPassOnly: callPassOnly ? 1 : 0
        });
        return params.toString();
    }

    const getHistory = async () => {
        try {
            setPopupOpen(true);
            const response = await axiosJWT.get(`${baseUrl}/removed-numbers-history?${buildQuery()}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setHistory(response.data.data);
            setSources(response.data.sources || {});
            setTotalItem(response.data.totalItem);
        } catch (error) {
            console.log(error);
        } finally {
            setPopupOpen(false);
        }
    }

    const getCompanyList = async () => {
        try {
            const response = await axiosJWT.get(`${baseUrl}/active-user-company`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setCompany(response.data.filter(item => item.id !== 1));
        } catch (error) {
            console.log(error);
        }
    }

    const handleSearch = (e) => {
        e.preventDefault();
        if (page !== 1) {
            setPage(1);
        } else {
            getHistory();
        }
    }

    const handleReset = (e) => {
        e.preventDefault();
        setSelectedCompany(0);
        setSearchTn('');
        setSelectedSource('');
        setFromDate('');
        setToDate('');
        setCallPassOnly(false);
        setPage(1);
    }

    const handleExport = async (e) => {
        e.preventDefault();
        try {
            setPopupOpen(true);
            const response = await axiosJWT.get(`${baseUrl}/removed-numbers-history-export?${buildQuery()}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, 'RemovedNumbersHistory.csv');
        } catch (error) {
            console.log(error);
        } finally {
            setPopupOpen(false);
        }
    }

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
    }

    const refreshToken = async () => {
        try {
            const response = await axios.get(`${baseUrl}/token`);

            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setCid(decoded.cid);
            setName(decoded.name);
            setRole(decoded.role);
            setExpire(decoded.exp);
        } catch (error) {
            if (error.response) {
                navigate("/");
                window.location.reload();
            }
        }
    }

    const getUserAccess = async () => {
        try {
            const response = await axiosJWT.get(`${baseUrl}/user-access`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setAccess(response.data.results);
        } catch (error) {
            console.log(error);
        }
    }

    const axiosJWT = axios.create();

    axiosJWT.interceptors.request.use(async (config) => {
        const currentDate = new Date();
        if (expire * 1000 < currentDate.getTime()) {
            const response = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${response.data.accessToken}`;
            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setCid(decoded.cid);
            setName(decoded.name);
            setRole(decoded.role);
            setExpire(decoded.exp);
        }
        return config;
    }, (error) => {
        return Promise.reject(error);
    });

    return (
        <div className="panelbox">
            <Navbar token={token} access={access} />
            <div className="right-panel">
                <Header name={name} />
                {popupOpen && (
                    <Loader />
                )}
                <div className="content-page">

                    <div className="row">
                        <div className="col-8">
                            <div className="left-title">
                                <h1>Removed Numbers History</h1>
                            </div>
                        </div>

                        <div className="col-4 top-right-buttons">
                            <div className="export-btn addnew-button"><a href="#" onClick={handleExport}>Export</a></div>
                            <div className="export-btn addnew-button"><Link to="/inventory"><span>&larr;</span> Back</Link></div>
                        </div>
                    </div>

                    <div className="common-white-shadow-background setting-section">
                        <div className="row">
                            <div className="col-2">
                                <label>Company</label>
                                <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)}>
                                    <option value={0}>All Companies</option>
                                    {company.map((item) => (
                                        <option key={item.id} value={item.id}>{item.cname}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-2">
                                <label>Telephone Number</label>
                                <input type="text" value={searchTn} onChange={(e) => setSearchTn(e.target.value)} placeholder="Search TN" />
                            </div>
                            <div className="col-2">
                                <label>Removed Via</label>
                                <select value={selectedSource} onChange={(e) => setSelectedSource(e.target.value)}>
                                    <option value="">All</option>
                                    {Object.keys(sources).map((key) => (
                                        <option key={key} value={key}>{sources[key]}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-2">
                                <label>From</label>
                                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                            </div>
                            <div className="col-2">
                                <label>To</label>
                                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                            </div>
                            <div className="col-2">
                                <label>
                                    <input type="checkbox" checked={callPassOnly} onChange={(e) => setCallPassOnly(e.target.checked)} />
                                    &nbsp;Call Pass only
                                </label>
                            </div>
                            <div className="col-12 flex self-center mt-3">
                                <button className="search-btn" onClick={handleSearch}>Search</button>
                                {(Number(selectedCompany) !== 0 || searchTn || selectedSource || fromDate || toDate || callPassOnly) && (<a className="search-btn clear-btn w-max" onClick={handleReset}>Clear</a>)}
                            </div>
                        </div>
                    </div>

                    <div className="common-white-shadow-background setting-section">
                        <div className="row">
                            <div className="setting-section haft-whitebox">
                                <div className="search-result-section">
                                    <h3>{totalItem} removed number(s)</h3>
                                </div>
                                <table className="tablebox search-result-table">
                                    <thead>
                                        <tr>
                                            <th>Removed (UTC)</th>
                                            <th>Telephone Number</th>
                                            <th>Customer</th>
                                            <th>Removed By</th>
                                            <th>Removed Via</th>
                                            <th>Call Pass</th>
                                            <th>Compliance Emailed</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((item, index) => (
                                            <tr key={index}>
                                                {/* BCP-32: BBD only page, so always UTC - role 0 selects that branch */}
                                                <td>{formatDateTimeToPST(item.removed_at, 'UTC', 0)}</td>
                                                <td>{item.tn_number}</td>
                                                <td>{item.cname || item.customer_name}</td>
                                                <td>{item.removed_by}</td>
                                                <td>{item.source_label}</td>
                                                <td>{item.call_pass_active == 1 ? 'Active' : (item.call_pass_enable_date ? 'Expired' : '-')}</td>
                                                <td>
                                                    {item.call_pass_active != 1
                                                        ? 'N/A'
                                                        : (item.email_sent == 1
                                                            ? 'Yes'
                                                            : <span title={item.email_error || ''}>No</span>)}
                                                </td>
                                            </tr>))}
                                        {history.length === 0 && (
                                            <tr><td colSpan={7}>No removed numbers found.</td></tr>
                                        )}
                                    </tbody>
                                </table>

                                {totalItem > 20 && (<Pagination
                                    activePage={page}
                                    itemsCountPerPage={20}
                                    totalItemsCount={totalItem}
                                    pageRangeDisplayed={5}
                                    onChange={handlePageChange.bind(this)}
                                />)}

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    )
}

export default RemovedNumbersHistory
